import { useRef, useState, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Routes } from "router/routes";
import { CaretDownIcon, ClipboardMagicpen, ConvertShape2, Element4, GreyClipboardwPen, HamburgerMenuIcon, MedalIcon, PeopleIconNew, PrecisionLogoMain } from "assets/website";
import { useAppSelector } from "state/hooks";
import { useFetchUser } from "hooks";
import { Signout } from "pages";

export type tabs = "home" | "pricing" | "howItWorks" | "useCases" | "faq" | "blog" | "earlyAccess";

export interface NavbarProps {
  active: tabs;
}

interface NavItems {
  title: string;
  key: tabs;
  link: string;
  hasDropdown?: boolean;
}

interface ProductMenuItem {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const productMenuItems: ProductMenuItem[] = [
  {
    icon: ClipboardMagicpen,
    title: "AI CV Screening & Ranking",
    description: "Precision evaluates every CV against your JD with exact skill-to-experience mapping."
  },
  {
    icon: GreyClipboardwPen,
    title: "Role-Based Competence Assessment",
    description: "Technical, soft skills, and work-style analysis are built automatically for every role."
  },
  {
    icon: MedalIcon,
    title: "Predictive Fit Scoring",
    description: "See the likelihood that a candidate will succeed in the role."
  },
  {
    icon: PeopleIconNew,
    title: "Collaboration",
    description: "Share candidate reports, add reviewers, leave comments, and decide as a team."
  },
  {
    icon: ConvertShape2,
    title: "Side-by-Side Comparisons",
    description: "Compare up to five candidates on every metric—experience, competence, culture fit, and more."
  },
  {
    icon: Element4,
    title: "Analytics Dashboard",
    description: "Precision evaluates every CV against your JD with exact skill-to-experience mapping."
  },
];

const Navbar: React.FC<NavbarProps> = ({ active }) => {
  const [showNav, setShowNav] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownTimeoutRef = useRef<number | null>(null);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const { getUser } = useFetchUser();

  const hasToken = Boolean(
    localStorage.getItem("precisionAccess") &&
    localStorage.getItem("precisionAccess") !== "undefined"
  );

  // Hydrate user from API if token exists but Redux state is empty
  useEffect(() => {
    if (hasToken && !user.id) {
      getUser();
    }
  }, [hasToken, user.id]);

  const isLoggedIn = hasToken && Boolean(user.id);

  // Close account dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowAccountDropdown(false);
    setShowNav(false);
    setShowLogoutModal(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 120;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: 'smooth'
      });
    }
  };

  const scrollToUseCases = () => {
    scrollToSection('use-cases');
  };

  const handleProductClick = (index: number) => {
    setShowProductsDropdown(false);
    if (location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('selectProduct', { detail: { productIndex: index } }));
      scrollToSection('products-section');
    } else {
      navigate('/', { state: { scrollToProducts: true, productIndex: index } });
    }
  };

  const handleUseCasesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/use-cases');
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowProductsDropdown(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowProductsDropdown(false);
    }, 100) as unknown as number;
  };

  const navLinks: NavItems[] = [
    {
      title: "How it Works",
      key: "howItWorks",
      link: "/how-it-works",
    },
    {
      title: "Use Cases",
      key: "useCases",
      link: "/use-cases",
    },
    {
      title: "Pricing",
      key: "pricing",
      link: "/pricing",
    },
  ];

  return (
    <>
      <header className={`pt-8 fixed w-screen top-0 z-[99] bg-transparent flex justify-center transition-transform duration-300 ease-in-out ${isVisible ? '' : '-translate-y-full'}`}>
        <section className="w-full h-full flex flex-col">
          <section
            className={`flex items-center justify-between rounded-[16px] px-6 lg:h-auto h-[70px] bg-[#050B17] w-[90%] mx-auto`}
          >
            <Link
              onClick={() => setShowNav(false)}
              className="h-auto -mb-1"
              to="/"
            >
              <PrecisionLogoMain className="md:w-[162px] sm:w-[140px] w-[110px] sm:mb-0 mb-1" />
            </Link>
            <nav className="lg:flex hidden gap-12 text-sm">
              {navLinks.map((item) => (
                item.hasDropdown ? (
                  <div
                    key={item.key}
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`flex items-center gap-1 text-[#F2F4F7] py-8 text-base`}
                    >
                      {item.title}
                      <CaretDownIcon className={`w-3 h-3 mt-1 transition-transform duration-200 ${showProductsDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-200 ease-out ${showProductsDropdown
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                        }`}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {/* Triangle pointer */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200 z-10"></div>
                      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 px-8 py-4 w-[500px] min-[990px]:w-[700px] min-[1200px]:w-[900px]">
                        <div className="grid grid-cols-2 gap-x-8">
                          {productMenuItems.map((product, index) => {
                            const isLastRow = index >= productMenuItems.length - 2;
                            const isLeftColumn = index % 2 === 0;
                            return (
                              <div
                                key={index}
                                className={`${!isLastRow ? 'border-b border-gray-200' : ''} ${isLeftColumn ? 'pr-4' : 'pl-4'}`}
                              >
                                <button
                                  onClick={() => handleProductClick(index)}
                                  className="flex items-center gap-4 py-6 w-full text-left transition-colors group"
                                >
                                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                    <product.icon
                                      className="w-6 h-6 transition-colors [&>path]:fill-[#475467] group-hover:[&>path]:fill-[#5F89DA] group-hover:[&>path]:stroke-[#5F89DA]"
                                    />
                                  </div>
                                  <span className="text-base font-normal flex-1 transition-colors text-[#344054] group-hover:text-[#5F89DA]">
                                    {product.title}
                                  </span>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : item.key === "useCases" ? (
                  <button
                    key={item.key}
                    onClick={handleUseCasesClick}
                    className={`text-[#F2F4F7] py-8 text-base ${item.key === active ? 'border-b-4 border-[#FC9F4C]' : ''}`}
                  >
                    {item.title}
                  </button>
                ) : (
                  <Link
                    className={`text-[#F2F4F7] py-8 text-base ${item.key === active ? 'border-b-4 border-[#FC9F4C]' : ''}`}
                    key={item.key}
                    to={item.link}
                  >
                    {item.title}
                  </Link>
                )
              ))}
            </nav>
            <div className="lg:flex hidden items-center gap-6">
              {isLoggedIn ? (
                <div className="relative" ref={accountDropdownRef}>
                  <button
                    onClick={() => setShowAccountDropdown((prev) => !prev)}
                    className="flex items-center gap-2"
                  >
                    {user.companyLogo ? (
                      <img
                        src={user.companyLogo}
                        alt="Company"
                        className="w-10 h-10 rounded-full object-cover border-2 border-white"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#012169]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                      </div>
                    )}
                    <ChevronDown className="w-4 h-4 text-[#98A2B3]" />
                  </button>

                  {showAccountDropdown && (
                    <div className="absolute right-0 top-full mt-3 w-52 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
                      <button
                        onClick={() => {
                          setShowAccountDropdown(false);
                          navigate(Routes.publishAssessment);
                        }}
                        className="w-full text-left px-5 py-3.5 text-[#344054] text-sm font-medium hover:bg-[#EFF4FA] border-l-4 border-transparent hover:border-[#012169] transition-colors"
                      >
                        View Account
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-5 py-3.5 text-[#D92D20] text-sm font-medium hover:bg-red-50 border-l-4 border-transparent transition-colors"
                      >
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to={Routes.signin} className="text-[#F2F4F7] text-base hover:text-white transition-colors">Sign In</Link>
                  <button onClick={() => navigate(Routes.signup)} className="py-3.5 px-6 rounded-md text-sm text-[#012169] bg-white font-medium">Try for Free</button>
                </>
              )}
            </div>
            <div
              onClick={() => setShowNav(!showNav)}
              className="lg:hidden cursor-pointer"
            >
              <HamburgerMenuIcon className="[&>path]:fill-[#FFFFFF]" />
            </div>
          </section>
        </section>
      </header>

      {/* Full-page Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[100] bg-[#050B17] lg:hidden transition-all duration-300 ease-in-out ${
          showNav ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full px-6 pb-8 pt-16">
          {/* Header with Logo and Close Button */}
          <div className="flex items-center justify-between mb-12 w-full">
            <Link
              onClick={() => setShowNav(false)}
              to={"/"}
            >
              <PrecisionLogoMain className="w-[150px]" />
            </Link>
            <button
              onClick={() => setShowNav(false)}
              className="w-6 h-6 flex items-center justify-center border border-white rounded-full"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1">
            {/* Products Accordion */}
            <div className="">
              <button
                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                className="flex items-center justify-between w-full py-4"
              >
                <span className="text-white text-2xl font-medium">Products</span>
                <CaretDownIcon className={`w-5 h-5 [&>path]:stroke-[#FFFFFF] transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-out ${
                  mobileProductsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="mb-4 bg-[#1D2939] rounded-md">
                  {productMenuItems.map((product, index) => (
                    <Link
                      key={index}
                      to="/"
                      className="flex items-center gap-3 py-3 px-4 bg-transparent rounded-md border-b border-[#344054]"
                    >
                      <product.icon className="w-5 h-5 [&>path]:fill-[#98A2B3]" />
                      <span className="text-[#F2F4F7] text-base">{product.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* How it Works Link */}
            <Link
              to="/how-it-works"
              onClick={() => setShowNav(false)}
              className={`block py-4 text-white text-2xl font-medium ${active === 'howItWorks' ? 'border-b-2 border-[#FC9F4C]' : ''}`}
            >
              How it Works
            </Link>

            {/* Use Cases Link */}
            <Link
              to="/use-cases"
              onClick={() => setShowNav(false)}
              className={`block py-4 text-white text-2xl font-medium ${active === 'useCases' ? 'border-b-2 border-[#FC9F4C]' : ''}`}
            >
              Use cases
            </Link>

            {/* Pricing Link */}
            <Link
              to={"/pricing"}
              onClick={() => setShowNav(false)}
              className={`block py-4 text-white text-2xl font-medium ${active === 'pricing' ? 'border-b-2 border-[#FC9F4C]' : ''}`}
            >
              Pricing
            </Link>
          </nav>

          {/* Auth Buttons */}
          <div className="mt-auto pb-8 space-y-3">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => { navigate(Routes.publishAssessment); setShowNav(false); }}
                  className="block w-full py-4 px-6 rounded-lg text-white bg-transparent font-semibold text-lg border-2 border-white text-center"
                >
                  View Account
                </button>
                <button
                  onClick={() => { handleLogout(); }}
                  className="w-full py-4 px-6 rounded-lg text-[#D92D20] bg-white font-semibold text-lg border-2 border-[#D92D20]"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to={Routes.signin}
                  onClick={() => setShowNav(false)}
                  className="block w-full py-4 px-6 rounded-lg text-white bg-transparent font-semibold text-lg border-2 border-white text-center"
                >
                  Sign In
                </Link>
                <button
                  onClick={() => { navigate(Routes.signup); setShowNav(false); }}
                  className="w-full py-4 px-6 rounded-lg text-[#012169] bg-white font-semibold text-lg border-2 border-[#012169]"
                >
                  Try for Free
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <Signout show={showLogoutModal} close={() => setShowLogoutModal(false)} />
    </>
  );
};

export { Navbar };
